import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { FormService, FormField, FormConfig } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useChoices } from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import {
  usePostMeterReadingStatus,
  useUpdateMeterReadingStatus,
} from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { convertKeysToSnakeCase } from "@shared/utils/caseConverter";
import { toast } from "@shared/ui/use-toast";
import { logEvent } from "@shared/analytics/analytics";

interface ReadingStatusAddEditFormProps {
  open: boolean;
  onOpenChange: () => void;
  initialData?: any;
}

const ReadingStatusAddEditForm: React.FC<ReadingStatusAddEditFormProps> = ({
  open,
  onOpenChange,
  initialData,
}) => {
  const { data: meterStatus } = useChoices({ choice: "meter_meter_reading_status" });
  const { mutate: createStatus } = usePostMeterReadingStatus();
  const { mutate: updateStatus } = useUpdateMeterReadingStatus();
  const remoteUtilityId = useRemoteUtilityId();
  const meterStatusList = mapToLabelValue(meterStatus);
  const [isEditing, setIsEditing] = useState(false);
  const startTimeRef = useRef<number | null>(null);

  const fields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Reading Status",
      required: true,
      placeholder: "Enter reading status",
    },
    {
      name: "codeId",
      type: "select",
      disabled: !!isEditing,
      readonly: isEditing,
      label: "System Reading Status",
      required: true,
      placeholder: "Select status",
      options: meterStatusList,
    },
    {
      name: "reason",
      type: "textarea",
      label: "Description",
      placeholder: "Enter description",
      rows: 3,
    },
  ];

  const formConfig: FormConfig = {
    fields,
    showResetButton: true,
    showSubmitButton: true,
    submitButtonText: "Submit",
    resetButtonText: "Cancel",
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      select: "rounded-md border px-3 py-2 border-gray-300",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
  };

  const form = FormService.createForm(formConfig);

  // TRACK FORM OPEN + START TIMER
  useEffect(() => {
    if (open) {
      const formType = initialData ? "Edit" : "Add";
      startTimeRef.current = Date.now();

      logEvent("MX Reading Status Form Opened", {
        formType,
      });
    }
  }, [open]);

  // TRACK FORM SUBMIT
  const handleSubmit = (data: any) => {
    const formType = isEditing ? "Edit" : "Add";
    const payload = { ...data, remoteUtilityId };
    const snakeCasePayload = convertKeysToSnakeCase(payload);
    const durationMs = startTimeRef.current ? Date.now() - startTimeRef.current : null;

    const commonLog = {
      formType,
      durationMs,
      durationSec: durationMs ? Math.round(durationMs / 1000) : undefined,
    };

    const onSuccess = () => {
toast({
  title: "Success",
  description: `Reading status ${isEditing ? "updated" : "added"} successfully.`,
  variant: "default",
});
      logEvent("MX Reading Status Form Submitted", {
        ...commonLog,
        ...snakeCasePayload,
        ...(isEditing ? { id: initialData?.id } : {}),
      });
    }; if (isEditing) {
      updateStatus({ id: initialData.id, payload: snakeCasePayload }, { onSuccess });
    } else {
      createStatus(snakeCasePayload, { onSuccess });
    }

    onOpenChange();
    setIsEditing(false);
  };

  // TRACK FORM CANCEL
  const handleCancel = () => {
    const formType = isEditing ? "Edit" : "Add";
    const durationMs = startTimeRef.current ? Date.now() - startTimeRef.current : null;

    logEvent("MX Reading Status Form Cancelled", {
      formType,
      durationMs,
      durationSec: durationMs ? Math.round(durationMs / 1000) : undefined,
    });

    onOpenChange();
    setIsEditing(false);
  };

  useEffect(() => {
    if (initialData && open) {
      setIsEditing(true);
      FormService.populateForm(form, initialData, fields);
    } else if (open) {
      setIsEditing(false);
      FormService.populateForm(form, "", fields);
    }
  }, [initialData, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reading Status</DialogTitle>
        </DialogHeader>
        <DynamicForm
          form={form}
          fields={fields}
          config={formConfig}
          gridColumns={1}
          onSubmit={handleSubmit}
          onReset={handleCancel}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ReadingStatusAddEditForm;
